import { useToast } from '@quadminds/mindkit';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { agentsApi } from '../services/agents-api.service';
import { FileItem } from '../types/File/File.types';

// Definir el tipo localmente o importarlo si lo exportas del provider
type DialogKeys =
    | 'isRenameDialogOpen'
    | 'isMoveDialogOpen'
    | 'isUploadModalOpen'
    | 'isPreviewModalOpen'
    | 'isDeleteDialogOpen'
    | 'isReplaceDialogOpen';

interface UseReplaceFileParams {
    handleDialogState: (dialog: DialogKeys, isOpen: boolean) => void;
    handleIsUploadingFile: (isUploading: boolean) => void;
    clearSelectItemToReplace: () => void;
}

const useReplaceFile = ({
    handleDialogState,
    handleIsUploadingFile,
    clearSelectItemToReplace,
}: UseReplaceFileParams) => {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    const replaceFileMutation = useMutation({
        mutationKey: ['replaceFile'],
        mutationFn: async ({ file, newFile }: { file: FileItem; newFile: File }) => {
            handleIsUploadingFile(true);
            return agentsApi.replaceFile(file.id, newFile);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['documents'] });
            toast({
                title: t('toast.replaceFile.success.title'),
                description: t('toast.replaceFile.success.description', { name: variables.file.name }),
                variant: 'default',
            });
            handleDialogState('isReplaceDialogOpen', false);
            clearSelectItemToReplace();
        },
        onError: (error, variables) => {
            console.error('Error replacing file', error);
            toast({
                title: t('toast.replaceFile.error.title'),
                description: t('toast.replaceFile.error.description', { name: variables.file.name }),
                variant: 'destructive',
            });
        },
        onSettled: () => {
            handleIsUploadingFile(false);
        },
    });

    return {
        replaceFileMutation,
    };
};

export default useReplaceFile;
